import React, { useState, useEffect } from 'react';
import { Modal, Button, Box, Group, Text, Loader, Center, TextInput, Textarea, Switch, MultiSelect, Stack, Divider, Grid, Paper, Title } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '@auth0/auth0-react';
import { notifications } from '@mantine/notifications';
import { IconDeviceFloppy, IconEdit, IconX, IconCalendar } from '@tabler/icons-react';
import { FileListItem, FileMetadataUpdate } from '../interfaces/types';
import { safeFetchJson } from '../utils/safeFetch';

interface DocumentViewerProps {
  isOpen: boolean;
  onClose: () => void;
  filename: string;
  fileUrl: string | null;
  uploadMetadata?: { 
    uploadedBy: string; 
    uploadedAt: string;
    wentThrough?: boolean;
    failureReason?: string;
    client?: string;
    lawyers?: string[];
    date?: string;
  } | null;
  isLoading: boolean;
  fileMetadata?: FileListItem | null;
  onMetadataUpdate?: (metadata: FileMetadataUpdate) => void;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({
  isOpen,
  onClose,
  filename,
  fileUrl,
  uploadMetadata,
  isLoading,
  fileMetadata,
  onMetadataUpdate
}) => {
  const { t } = useTranslation();
  const { getAccessTokenSilently } = useAuth0();
  
  // Editable metadata state
  const [isEditing, setIsEditing] = useState(false);
  const [editedMetadata, setEditedMetadata] = useState<Partial<FileMetadataUpdate>>({});
  const [availableLawyers, setAvailableLawyers] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  // Initialize edited metadata when file metadata changes
  useEffect(() => {
    // Use uploadMetadata (fresh from DynamoDB) if available, fallback to fileMetadata
    const metadata = uploadMetadata || fileMetadata;
    if (metadata) {
      setEditedMetadata({
        wentThrough: metadata.wentThrough,
        failureReason: metadata.failureReason || '',
        client: metadata.client || '',
        lawyers: metadata.lawyers || [],
        date: metadata.date || ''
      });
    }
  }, [uploadMetadata, fileMetadata]);

  // Fetch available lawyers for MultiSelect
  useEffect(() => {
    if (isOpen) {
      fetchAvailableLawyers();
    }
  }, [isOpen]);

  const fetchAvailableLawyers = async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await safeFetchJson(`${import.meta.env.VITE_API_URL}/lawyerData/listLawyers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableLawyers(response.map((lawyer: any) => lawyer.LawyerName));
    } catch (error) {
      console.error('Failed to fetch lawyers:', error);
    }
  };

  const handleSaveMetadata = async () => {
    if (!onMetadataUpdate) return;
    
    setSaving(true);
    try {
      const updateData: FileMetadataUpdate = {
        filename,
        ...editedMetadata
      };
      
      await onMetadataUpdate(updateData);
      
      notifications.show({
        title: t('success'),
        message: t('metadataUpdatedSuccessfully'),
        color: 'green'
      });
      
      setIsEditing(false);
    } catch (error) {
      notifications.show({
        title: t('error'),
        message: t('failedToUpdateMetadata'),
        color: 'red'
      });
    } finally {
      setSaving(false);
    }
  };

  const getFileExtension = (filename: string): string => {
    return filename.split('.').pop()?.toLowerCase() || '';
  };

  const renderFileContent = () => {
    if (isLoading) {
      return (
        <Center style={{ height: '400px' }}>
          <Loader size="lg" />
        </Center>
      );
    }

    if (!fileUrl) {
      return (
        <Text c="red" ta="center" p="md">
          {t('errorLoadingFile')}
        </Text>
      );
    }

    const extension = getFileExtension(filename);

    switch (extension) {
      case 'pdf':
        return (
          <iframe
            src={fileUrl}
            width="100%"
            height="600px"
            style={{ border: 'none' }}
            title={filename}
          />
        );
      case 'txt':
        return (
          <iframe
            src={fileUrl}
            width="100%"
            height="600px"
            style={{ border: '1px solid #e0e0e0', borderRadius: '4px' }}
            title={filename}
          />
        );
      case 'jpg':
      case 'jpeg':
      case 'png':
        return (
          <Box ta="center" p="md">
            <img
              src={fileUrl}
              alt={filename}
              style={{ maxWidth: '100%', maxHeight: '600px', objectFit: 'contain' }}
            />
          </Box>
        );
      case 'doc':
      case 'docx':
      case 'pptx':
        return (
          <Box p="md" ta="center">
            <Text mb="md">
              {t('officeFileViewing', { filename })}
            </Text>
            <Button
              component="a"
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="filled"
            >
              {t('downloadAndView')}
            </Button>
          </Box>
        );
      default:
        return (
          <Box p="md" ta="center">
            <Text mb="md">
              {t('unsupportedFileType', { extension })}
            </Text>
            <Button
              component="a"
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="filled"
            >
              {t('downloadFile')}
            </Button>
          </Box>
        );
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const formatDateForInput = (dateString: string) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toISOString().split('T')[0]; // YYYY-MM-DD format for input[type="date"]
    } catch {
      return dateString;
    }
  };

  const formatDateForDisplay = (dateString: string) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const renderMetadataPanel = () => {
    const metadata = uploadMetadata || fileMetadata;
    if (!metadata) return null;

    return (
      <Paper p="md" withBorder>
        <Group justify="space-between" mb="md">
          <Title order={4}>{t('fileMetadata')}</Title>
          <Group>
            {!isEditing ? (
              <Button
                leftSection={<IconEdit size={16} />}
                variant="light"
                onClick={() => setIsEditing(true)}
              >
                {t('edit')}
              </Button>
            ) : (
              <Group>
                <Button
                  leftSection={<IconDeviceFloppy size={16} />}
                  onClick={handleSaveMetadata}
                  loading={saving}
                >
                  {t('save')}
                </Button>
                <Button
                  leftSection={<IconX size={16} />}
                  variant="outline"
                  onClick={() => {
                    setIsEditing(false);
                    // Reset to original values
                    if (metadata) {
                      setEditedMetadata({
                        wentThrough: metadata.wentThrough,
                        failureReason: metadata.failureReason || '',
                        client: metadata.client || '',
                        lawyers: metadata.lawyers || [],
                        date: metadata.date || '',
                      });
                    }
                  }}
                >
                  {t('cancel')}
                </Button>
              </Group>
            )}
          </Group>
        </Group>

        <Stack gap="md">
          {/* Upload Information */}
          <Grid>
            <Grid.Col span={6}>
              <Text size="sm" fw={500}>{t('uploadedBy')}:</Text>
              <Text size="sm">{uploadMetadata?.uploadedBy || 'Unknown'}</Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text size="sm" fw={500}>{t('uploadedAt')}:</Text>
              <Text size="sm">{uploadMetadata?.uploadedAt ? formatDate(uploadMetadata.uploadedAt) : 'Unknown'}</Text>
            </Grid.Col>
          </Grid>

          <Divider />

          {/* Editable Metadata */}
          <Grid>
            <Grid.Col span={6}>
              <Text size="sm" fw={500}>{t('wentThrough')}:</Text>
              {isEditing ? (
                <Switch
                  checked={editedMetadata.wentThrough || false}
                  onChange={(event) => setEditedMetadata(prev => ({ 
                    ...prev, 
                    wentThrough: event.currentTarget.checked,
                    // Clear failure reason if switching to "went through"
                    failureReason: event.currentTarget.checked ? '' : prev.failureReason
                  }))}
                  label={editedMetadata.wentThrough ? t('yes') : t('no')}
                />
              ) : (
                <Text size="sm" c={metadata.wentThrough === true ? 'green' : metadata.wentThrough === false ? 'red' : 'dimmed'}>
                  {metadata.wentThrough === true ? t('yes') : metadata.wentThrough === false ? t('no') : t('unknown')}
                </Text>
              )}
            </Grid.Col>
            <Grid.Col span={6}>
              <Text size="sm" fw={500}>{t('client')}:</Text>
              {isEditing ? (
                <TextInput
                  value={editedMetadata.client || ''}
                  onChange={(event) => setEditedMetadata(prev => ({ 
                    ...prev, 
                    client: event.target.value 
                  }))}
                  placeholder={t('enterClientName')}
                  size="sm"
                />
              ) : (
                <Text size="sm">{metadata.client || '-'}</Text>
              )}
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={6}>
              <Text size="sm" fw={500}>{t('extractedDate')}:</Text>
              {isEditing ? (
                <TextInput
                  type="date"
                  value={editedMetadata.date ? formatDateForInput(editedMetadata.date) : ''}
                  onChange={(event) => setEditedMetadata(prev => ({ 
                    ...prev, 
                    date: event.target.value // HTML date input gives YYYY-MM-DD format
                  }))}
                  placeholder={t('enterDate')}
                  size="sm"
                  leftSection={<IconCalendar size={16} />}
                />
              ) : (
                <Text size="sm">{metadata.date ? formatDateForDisplay(metadata.date) : '-'}</Text>
              )}
            </Grid.Col>
            <Grid.Col span={6}>
              <Text size="sm" fw={500}>{t('lawyers')}:</Text>
              {isEditing ? (
                <MultiSelect
                  value={editedMetadata.lawyers || []}
                  onChange={(value) => setEditedMetadata(prev => ({ 
                    ...prev, 
                    lawyers: value 
                  }))}
                  data={availableLawyers}
                  placeholder={t('selectLawyers')}
                  searchable
                  size="sm"
                />
              ) : (
                <Text size="sm">{metadata.lawyers?.join(', ') || '-'}</Text>
              )}
            </Grid.Col>
          </Grid>

                      {((isEditing && editedMetadata.wentThrough === false) || (!isEditing && metadata.wentThrough === false && metadata.failureReason)) && (
            <Box>
              <Text size="sm" fw={500} mb="xs">{t('failureReason')}:</Text>
              {isEditing ? (
                <Textarea
                  value={editedMetadata.failureReason || ''}
                  onChange={(event) => setEditedMetadata(prev => ({ 
                    ...prev, 
                    failureReason: event.target.value 
                  }))}
                  placeholder={t('explainWhyDidntGoThrough')}
                  minRows={2}
                  size="sm"
                />
              ) : (
                                  <Text size="sm">{metadata.failureReason || '-'}</Text>
              )}
            </Box>
          )}
        </Stack>
      </Paper>
    );
  };

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title={filename}
      size="xl"
      styles={{
        body: { padding: 0 },
        header: { padding: '1rem' }
      }}
    >
      <Box p="md">
        <Grid>
          <Grid.Col span={8}>
            <Box pos="relative" mih={400}>
              {renderFileContent()}
            </Box>
          </Grid.Col>
          <Grid.Col span={4}>
            {renderMetadataPanel()}
          </Grid.Col>
        </Grid>
        
        <Group justify="flex-end" mt="md">
          <Button variant="outline" onClick={onClose}>
            {t('close')}
          </Button>
          {fileUrl && (
            <Button
              component="a"
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="filled"
            >
              {t('openInNewTab')}
            </Button>
          )}
        </Group>
      </Box>
    </Modal>
  );
};

export default DocumentViewer; 